export interface CouponTemplateDto {
  couponTemplateNumber: string;
  name: string;
  status: CouponTemplateStatus;
  codeType: CodeType;
  activationType: ActivationType;
  valueType: ValueType;
  value: string;
  validityType?: string;
  expirationDate?: string;
  validityPeriodDays?: number;
  maxCouponsPerMember?: number;
  requiredPoints?: number;
  minimumPurchaseAmount?: number;
  eligibilityType?: EligibilityType;
  eligibilities?: string[];
  operationalUnits?: string[];
  presentationType?: PresentationType;
  displayedCouponName?: string;
  description?: string;
  couponImageUrl?: string;
  infoTextForEndUsers?: string;
}

export enum PresentationType {
  CODE_ONLY = 'CODE_ONLY',
  INAPP_COUPON = 'IN_APP_COUPON'
}

export enum CouponTemplateStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

export enum CodeType {
  INDIVIDUAL_GENERATED = 'INDIVIDUAL_GENERATED',
  INDIVIDUAL_UPLOADED = 'INDIVIDUAL_UPLOADED',
  GENERIC = 'GENERIC'
}

export enum ActivationType {
  FREE = 'FREE',
  POINTS = 'POINTS'
}

export enum ValueType {
  PERCENTAGE = 'PERCENTAGE',
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  GIFT = 'GIFT',
  FREE_SHIPPING = 'FREE_SHIPPING'
}

export enum EligibilityType {
  NONE = 'NONE',
  ALL = 'ALL',
  SEGMENT = 'SEGMENT',
  STATUS_CIRCLE = 'STATUS_CIRCLE'
}

export enum CouponStatusAfterIssuance {
  ACTIVE = 'ACTIVATED',
  INACTIVE = 'INACTIVE'
}

// Backend response interface (matching CouponTemplateDetailsResponse.java)
export interface CouponTemplateApiResponse {
  couponNumber: string;
  couponTemplateNumber: string;
  name: string;
  codeType: string;
  activationType: string;
  requiredPoints?: number;
  status: string;
  valueType: string;
  discountPercentage?: number;
  discountAmount?: number;
  codePrefix?: string;
  codeLength?: number;
  uploadFileName?: string;
  genericCode?: string;
  validityType?: string;
  validTo?: string;
  expirationDate?: string;
  validityPeriodDays?: number;
  maxCouponsPerMember?: number;
  minimumPurchaseAmount?: number;
  eligibilityType?: string;
  eligibilities?: string[];
  operationalUnits?: string[];
  // InApp Coupon fields
  presentationType?: string;
  displayedCouponName?: string;
  description?: string;
  couponImageUrl?: string;
  infoTextForEndUsers?: string;
  initialCouponStatus?: string;
  initialActivationState?: string;
}

export interface OperationalUnit {
  id: string;
  name: string;
  code: string;
  children?: OperationalUnit[];
}

export interface CouponTemplateAssignmentDto {
  eligibilityType: EligibilityType;
  eligibilityNumbers?: string[];
}

export enum DesignType {
  NONE = 'NONE',
  CUSTOM = 'CUSTOM'
}

export interface CouponTemplateFormDto {
  name: string;
  // Reward and Value
  valueType: ValueType;
  value: string;
  minimumPurchaseAmount?: number;
  // Coupon Type
  activationType: ActivationType;
  requiredPoints?: number;
  initialCouponStatus: CouponStatusAfterIssuance;
  // Validity
  validityType: string;
  validityPeriodDays?: number;
  // Redeemable At
  operationalUnitNumbers?: string[];
  // Code Generation
  codeType: CodeType;
  codePrefix?: string;
  codeLength?: number;
  couponCode?: string;
  // Design
  designType: DesignType;
  displayedCouponName?: string;
  description?: string;
  couponImage?: File | string;
  infoTextForEndUsers?: string;
  // Legacy/internal fields
  uploadFileName?: string;
  eligibilityType?: EligibilityType;
  eligibilityNumbers?: string[];
}

export interface CreateCouponTemplateRequest {
  name: string;
  codeType: CodeType;
  activationType: ActivationType;
  requiredPoints?: number;
  valueType: ValueType;
  discountPercentage?: string;
  discountAmount?: string;
  codePrefix?: string;
  codeLength?: number;
  uploadFileName?: string;
  genericCode?: string;
  validityPeriodDays?: number;
  minimumPurchaseAmount?: string;
  operationalUnitNumbers?: string[];
  // InApp Coupon fields
  presentationType: PresentationType;
  description?: string;
  couponImage?: File | string;
  infoTextForEndUsers?: string;
  initialCouponStatus: CouponStatusAfterIssuance;
}

// Map backend API response to frontend form data
export function mapCouponTemplateApiResponseToForm(apiResponse: CouponTemplateApiResponse): CouponTemplateFormDto {
  const formData: CouponTemplateFormDto = {
    name: apiResponse.name,
    // Reward and Value
    valueType: apiResponse.valueType as ValueType,
    value: '',
    minimumPurchaseAmount: apiResponse.minimumPurchaseAmount,
    // Coupon Type
    activationType: apiResponse.activationType as ActivationType,
    requiredPoints: apiResponse.requiredPoints,
    initialCouponStatus: (apiResponse.initialActivationState || apiResponse.initialCouponStatus)
      ? ((apiResponse.initialActivationState || apiResponse.initialCouponStatus) as CouponStatusAfterIssuance)
      : CouponStatusAfterIssuance.ACTIVE,
    // Validity - always DAYS_FROM_ACTIVATION
    validityType: 'DAYS_FROM_ACTIVATION',
    validityPeriodDays: apiResponse.validityPeriodDays || 30,
    // Redeemable At
    operationalUnitNumbers: apiResponse.operationalUnits || [],
    // Code Generation
    codeType: apiResponse.codeType as CodeType,
    codePrefix: apiResponse.codePrefix || '',
    codeLength: apiResponse.codeLength,
    couponCode: apiResponse.genericCode || '',
    // Design - map presentationType to designType
    designType: (apiResponse.presentationType as PresentationType) === PresentationType.INAPP_COUPON
      ? DesignType.CUSTOM
      : DesignType.NONE,
    displayedCouponName: apiResponse.displayedCouponName || '',
    description: apiResponse.description || '',
    couponImage: apiResponse.couponImageUrl || '',
    infoTextForEndUsers: apiResponse.infoTextForEndUsers || '',
    // Legacy
    uploadFileName: apiResponse.uploadFileName || '',
    eligibilityType: (apiResponse.eligibilityType as EligibilityType) || EligibilityType.NONE,
    eligibilityNumbers: apiResponse.eligibilities || [],
  };

  // Set value based on value type
  if (apiResponse.valueType === 'PERCENTAGE' && apiResponse.discountPercentage) {
    formData.value = String(apiResponse.discountPercentage);
  } else if (apiResponse.valueType === 'FIXED_AMOUNT' && apiResponse.discountAmount) {
    formData.value = String(apiResponse.discountAmount);
  } else if (apiResponse.valueType === 'GIFT' || apiResponse.valueType === 'FREE_SHIPPING') {
    formData.value = '';
  }

  return formData;
}

// Utility function to map frontend form data to backend API structure
export function mapCouponTemplateFormToCreateRequest(formData: CouponTemplateFormDto): CreateCouponTemplateRequest {
  const request: CreateCouponTemplateRequest = {
    name: formData.name,
    codeType: formData.codeType,
    activationType: formData.activationType,
    valueType: formData.valueType,
    minimumPurchaseAmount: formData.minimumPurchaseAmount ? String(formData.minimumPurchaseAmount) : undefined,
    validityPeriodDays: formData.validityPeriodDays,
    presentationType: formData.designType === DesignType.CUSTOM ? PresentationType.INAPP_COUPON : PresentationType.CODE_ONLY,
    initialCouponStatus: formData.initialCouponStatus
  };

  // Apply various mapping transformations
  mapActivationTypeData(formData, request);
  mapValueTypeData(formData, request);
  mapCodeConfiguration(formData, request);
  mapOperationalUnits(formData, request);
  mapInAppCouponData(formData, request);

  return request;
}

// Handle required points for POINTS activation type
function mapActivationTypeData(formData: CouponTemplateFormDto, request: CreateCouponTemplateRequest): void {
  if (formData.activationType === ActivationType.POINTS && formData.requiredPoints) {
    request.requiredPoints = formData.requiredPoints;
  }
}

// Handle value based on type
function mapValueTypeData(formData: CouponTemplateFormDto, request: CreateCouponTemplateRequest): void {
  if (formData.valueType === ValueType.PERCENTAGE) {
    request.discountPercentage = formData.value;
  } else if (formData.valueType === ValueType.FIXED_AMOUNT) {
    request.discountAmount = formData.value;
  }
}

// Handle code configuration
function mapCodeConfiguration(formData: CouponTemplateFormDto, request: CreateCouponTemplateRequest): void {
  if (formData.codeType === CodeType.INDIVIDUAL_GENERATED) {
    mapIndividualGeneratedCode(formData, request);
  } else if (formData.codeType === CodeType.GENERIC && formData.couponCode) {
    request.genericCode = formData.couponCode;
  }
}

// Handle individual generated code configuration
function mapIndividualGeneratedCode(formData: CouponTemplateFormDto, request: CreateCouponTemplateRequest): void {
  if (formData.codePrefix) {
    request.codePrefix = formData.codePrefix;
  }
  if (formData.codeLength) {
    request.codeLength = formData.codeLength;
  }
}


// Handle operational units
function mapOperationalUnits(formData: CouponTemplateFormDto, request: CreateCouponTemplateRequest): void {
  if (formData.operationalUnitNumbers) {
    request.operationalUnitNumbers = formData.operationalUnitNumbers;
  }
}

// Handle InApp coupon fields
function mapInAppCouponData(formData: CouponTemplateFormDto, request: CreateCouponTemplateRequest): void {
  // Only map InApp coupon fields if custom design is selected
  if (formData.designType === DesignType.CUSTOM) {
    mapInAppCouponFields(formData, request);
  }
}

// Handle specific InApp coupon field mapping
function mapInAppCouponFields(formData: CouponTemplateFormDto, request: CreateCouponTemplateRequest): void {
  if (formData.description) {
    request.description = formData.description;
  }
  // Only include couponImage if it's a File (new image selected by user)
  if (formData.couponImage instanceof File) {
    request.couponImage = formData.couponImage;
  }
  if (formData.infoTextForEndUsers) {
    request.infoTextForEndUsers = formData.infoTextForEndUsers;
  }
}